<template>
    <div class="va-page relative min-h-screen w-full">
        <div class="container mx-auto px-4 sm:px-8 py-8">

            <!-- Breadcrumb -->
            <nav class="text-sm text-gray-500 mb-5 flex items-center flex-wrap">
                <router-link to="/dashboard" class="hover:text-blue-700 font-medium">Dashboard</router-link>
                <span class="mx-1.5 text-gray-300">/</span>
                <span class="text-gray-800 font-semibold">Volunteering & Rewards Hub</span>
            </nav>

            <!-- Header -->
            <div class="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8">
                <div>
                    <p class="text-xs font-bold uppercase tracking-widest text-blue-600 m-0 mb-1">Engage+</p>
                    <h1 class="text-3xl font-extrabold text-gray-900 m-0">Volunteering & Rewards Hub</h1>
                    <p class="text-gray-500 mt-1 mb-0 text-sm">Create opportunities, review applications, and verify volunteering hours.</p>
                </div>
                <div class="flex items-center gap-3 flex-wrap">
                    <button
                        v-if="hasPermission('volunteering-application-index') && activeSection === 'applications'"
                        type="button"
                        class="va-btn-ghost"
                        :disabled="exporting"
                        @click="exportReport"
                    >
                        <font-awesome-icon :icon="['fas', 'file-excel']" />
                        <span>{{ exporting ? 'Exporting...' : 'Export Report' }}</span>
                    </button>
                    <button
                        v-if="canCreate && activeSection === 'opportunities'"
                        type="button"
                        class="va-btn-primary"
                        @click="openCreateModal"
                    >
                        <font-awesome-icon :icon="['fas', 'plus']" />
                        <span>Create Opportunity</span>
                    </button>
                </div>
            </div>

            <!-- Stats -->
            <div class="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
                <div class="va-stat-card va-stat-card--total">
                    <div class="va-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'bullhorn']" />
                    </div>
                    <div class="va-stat-card__body">
                        <p class="va-stat-card__value">{{ stats.opportunities }}</p>
                        <p class="va-stat-card__label">Opportunities</p>
                    </div>
                </div>
                <div class="va-stat-card va-stat-card--published">
                    <div class="va-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'check-circle']" />
                    </div>
                    <div class="va-stat-card__body">
                        <p class="va-stat-card__value">{{ stats.published }}</p>
                        <p class="va-stat-card__label">Published</p>
                    </div>
                </div>
                <div class="va-stat-card va-stat-card--pending" @click="goToSection('applications')">
                    <div class="va-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'clipboard-list']" />
                    </div>
                    <div class="va-stat-card__body">
                        <p class="va-stat-card__value">{{ stats.pendingApplications }}</p>
                        <p class="va-stat-card__label">Pending Applications</p>
                    </div>
                </div>
                <div class="va-stat-card va-stat-card--hours" @click="goToSection('hours')">
                    <div class="va-stat-card__icon">
                        <font-awesome-icon :icon="['fas', 'clock']" />
                    </div>
                    <div class="va-stat-card__body">
                        <p class="va-stat-card__value">{{ stats.pendingHours }}</p>
                        <p class="va-stat-card__label">Pending Hour Reviews</p>
                    </div>
                </div>
            </div>

            <!-- Tabs -->
            <div class="va-tabs-bar mb-5">
                <button
                    v-for="tab in sections"
                    :key="tab.value"
                    type="button"
                    class="va-tab"
                    :class="{ 'va-tab--active': activeSection === tab.value }"
                    @click="activeSection = tab.value"
                >
                    <font-awesome-icon :icon="tab.icon" class="va-tab__icon" />
                    <span>{{ tab.label }}</span>
                    <span v-if="tab.badge" class="va-tab__badge">{{ tab.badge }}</span>
                </button>
            </div>

            <!-- Opportunities -->
            <section v-if="activeSection === 'opportunities'" class="va-panel">
                <div class="va-panel__header">
                    <div>
                        <h2 class="va-panel__title">Opportunities</h2>
                        <p class="va-panel__subtitle">Manage volunteering events and publication status.</p>
                    </div>
                    <div class="va-filters">
                        <select v-if="canViewAll" v-model="listScope" class="va-select" @change="fetchOpportunities">
                            <option value="mine">My Opportunities</option>
                            <option value="all">All Opportunities</option>
                        </select>
                        <select v-model="statusFilter" class="va-select" @change="fetchOpportunities">
                            <option value="">All Statuses</option>
                            <option value="draft">Draft</option>
                            <option value="published">Published</option>
                            <option value="closed">Closed</option>
                            <option value="completed">Completed</option>
                            <option value="archived">Archived</option>
                        </select>
                    </div>
                </div>

                <div v-if="loading" class="va-panel__body">
                    <table-shimmer :titleHeaders="opportunityTableHeaders" :rows="6" />
                </div>
                <div v-else-if="opportunities.length === 0" class="va-empty">
                    <font-awesome-icon :icon="['fas', 'inbox']" class="va-empty__icon" />
                    <p class="va-empty__title">No opportunities found</p>
                    <p class="va-empty__text">Create a new opportunity or adjust your filters.</p>
                    <button v-if="canCreate" type="button" class="va-btn-primary mt-4" @click="openCreateModal">
                        <font-awesome-icon :icon="['fas', 'plus']" />
                        <span>Create Opportunity</span>
                    </button>
                </div>
                <div v-else>
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-left text-sm">
                            <thead>
                                <tr class="va-thead-row">
                                    <th class="va-th va-th-num">#</th>
                                    <th class="va-th">Title</th>
                                    <th v-if="isSuperAdmin" class="va-th">Created By</th>
                                    <th class="va-th">Status</th>
                                    <th class="va-th">Applications</th>
                                    <th class="va-th">Deadline</th>
                                    <th class="va-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(item, index) in opportunities" :key="item.id" class="va-row">
                                    <td class="va-td va-td-num">{{ paginatedRowNumber(oppPaginator, index) }}</td>
                                    <td class="va-td">
                                        <router-link :to="'/volunteering-and-rewards-hub/' + item.token" class="va-name-link">
                                            {{ item.title }}
                                        </router-link>
                                    </td>
                                    <td v-if="isSuperAdmin" class="va-td text-gray-600">
                                        {{ (item.created_by_user && item.created_by_user.name) ? item.created_by_user.name : '—' }}
                                    </td>
                                    <td class="va-td">
                                        <span class="va-badge" :class="'va-badge--' + item.status">{{ formatStatus(item.status) }}</span>
                                    </td>
                                    <td class="va-td text-gray-600">
                                        <router-link
                                            v-if="hasPermission('volunteering-application-index')"
                                            :to="'/volunteering-and-rewards-hub/' + item.token + '/applications'"
                                            class="va-apps-link"
                                            title="View Applications"
                                        >
                                            <span class="font-semibold text-gray-800">{{ item.approved_applications_count || 0 }}</span>
                                            <span class="text-gray-400"> / {{ item.max_volunteers || '∞' }}</span>
                                            <span v-if="item.pending_applications_count" class="va-inline-pill">{{ item.pending_applications_count }} pending</span>
                                        </router-link>
                                        <template v-else>
                                            <span class="font-semibold text-gray-800">{{ item.approved_applications_count || 0 }}</span>
                                            <span class="text-gray-400"> / {{ item.max_volunteers || '∞' }}</span>
                                            <span v-if="item.pending_applications_count" class="va-inline-pill">{{ item.pending_applications_count }} pending</span>
                                        </template>
                                    </td>
                                    <td class="va-td text-gray-500 whitespace-nowrap">{{ formatDateTime(item.application_deadline) }}</td>
                                    <td class="va-td">
                                        <div class="flex gap-1 flex-wrap items-center">
                                            <router-link
                                                v-if="hasPermission('volunteering-application-index')"
                                                :to="'/volunteering-and-rewards-hub/' + item.token + '/applications'"
                                                class="va-text-link"
                                                title="View Applications"
                                            >
                                                View Applications
                                            </router-link>
                                            <button type="button" class="va-action-btn" title="Edit" @click="editOpportunity(item)">
                                                <font-awesome-icon :icon="['fas', 'pen']" />
                                            </button>
                                            <button
                                                v-if="canPublish && item.status === 'draft'"
                                                type="button"
                                                class="va-action-btn va-action-btn--success"
                                                title="Publish"
                                                @click="updateStatus(item, 'published')"
                                            >
                                                <font-awesome-icon :icon="['fas', 'paper-plane']" />
                                            </button>
                                            <button
                                                v-if="canPublish && item.status === 'published'"
                                                type="button"
                                                class="va-action-btn"
                                                title="Close"
                                                @click="updateStatus(item, 'closed')"
                                            >
                                                <font-awesome-icon :icon="['fas', 'lock']" />
                                            </button>
                                            <button
                                                v-if="canDelete"
                                                type="button"
                                                class="va-action-btn va-action-btn--danger"
                                                title="Delete"
                                                @click="deleteOpportunity(item)"
                                            >
                                                <font-awesome-icon :icon="['fas', 'trash']" />
                                            </button>
                                            <button
                                                v-if="canResendVolunteeringEmail && item.status === 'published'"
                                                type="button"
                                                class="va-action-btn"
                                                title="Resend Email"
                                                @click="openResendEmail(item)"
                                            >
                                                <font-awesome-icon :icon="['fas', 'envelope']" />
                                            </button>
                                        </div>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="va-panel__footer">
                        <pagination v-if="oppPaginator" :paginator-info="oppPaginator" @page-changed="onOppPageChange" />
                    </div>
                </div>
            </section>

            <!-- Applications -->
            <section v-if="activeSection === 'applications'" class="va-panel">
                <div class="va-panel__header">
                    <div>
                        <h2 class="va-panel__title">Applications</h2>
                        <p class="va-panel__subtitle">Review student applications and export reports.</p>
                    </div>
                    <div class="va-filters">
                        <select v-model="appStatusFilter" class="va-select" @change="onAppFiltersChange">
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="returned">Returned</option>
                            <option value="rejected">Rejected</option>
                        </select>
                        <div class="va-search-wrap">
                            <font-awesome-icon :icon="['fas', 'search']" class="va-search-icon" />
                            <input
                                v-model="studentSearch"
                                type="text"
                                placeholder="Search student..."
                                class="va-input va-search-input"
                                @keyup.enter="onAppFiltersChange"
                            />
                        </div>
                        <button type="button" class="va-btn-ghost" @click="onAppFiltersChange">Search</button>
                    </div>
                </div>

                <div v-if="appOpportunityFilter" class="va-filter-chip-bar">
                    <span class="va-filter-chip">
                        Filtered by opportunity
                        <strong v-if="appOpportunityFilterTitle">{{ appOpportunityFilterTitle }}</strong>
                        <button type="button" class="va-filter-chip__clear" title="Clear filter" @click="clearOpportunityApplicationFilter">
                            <font-awesome-icon :icon="['fas', 'times']" />
                        </button>
                    </span>
                </div>

                <div v-if="loading" class="va-panel__body">
                    <table-shimmer :titleHeaders="['#', 'Student', 'Opportunity', 'Status', 'Submitted', 'Actions']" :rows="6" />
                </div>
                <div v-else-if="applications.length === 0" class="va-empty">
                    <font-awesome-icon :icon="['fas', 'user-clock']" class="va-empty__icon" />
                    <p class="va-empty__title">No applications found</p>
                    <p class="va-empty__text">Try adjusting your status filter or search term.</p>
                </div>
                <div v-else>
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-left text-sm">
                            <thead>
                                <tr class="va-thead-row">
                                    <th class="va-th va-th-num">#</th>
                                    <th class="va-th">Student</th>
                                    <th class="va-th">Opportunity</th>
                                    <th class="va-th">Status</th>
                                    <th class="va-th">Submitted</th>
                                    <th class="va-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(app, index) in applications" :key="app.id" class="va-row">
                                    <td class="va-td va-td-num">{{ paginatedRowNumber(appPaginator, index) }}</td>
                                    <td class="va-td">
                                        <p class="font-semibold text-gray-900 m-0">{{ app.user && app.user.name }}</p>
                                        <p class="text-xs text-gray-500 m-0 mt-0.5">{{ app.student_id }}</p>
                                    </td>
                                    <td class="va-td text-gray-700">{{ app.opportunity && app.opportunity.title }}</td>
                                    <td class="va-td">
                                        <span class="va-badge" :class="'va-badge--' + app.status">{{ formatStatus(app.status) }}</span>
                                    </td>
                                    <td class="va-td text-gray-500 whitespace-nowrap">{{ formatDateTime(app.submitted_at) }}</td>
                                    <td class="va-td">
                                        <router-link
                                            v-if="app.opportunity"
                                            :to="'/volunteering-and-rewards-hub/' + app.opportunity.token + '?application=' + app.id"
                                            class="va-text-link"
                                            title="View Application"
                                        >
                                            View Application
                                        </router-link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="va-panel__footer">
                        <pagination v-if="appPaginator" :paginator-info="appPaginator" @page-changed="onAppPageChange" />
                    </div>
                </div>
            </section>

            <!-- Hours -->
            <section v-if="activeSection === 'hours'" class="va-panel">
                <div class="va-panel__header">
                    <div>
                        <h2 class="va-panel__title">Hour Submissions</h2>
                        <p class="va-panel__subtitle">Verify submitted hours and credit student ledgers.</p>
                    </div>
                    <div class="va-filters">
                        <select v-model="hoursStatusFilter" class="va-select" @change="fetchHours">
                            <option value="">All Statuses</option>
                            <option value="pending">Pending</option>
                            <option value="approved">Approved</option>
                            <option value="returned">Returned</option>
                            <option value="rejected">Rejected</option>
                        </select>
                    </div>
                </div>

                <div v-if="loading" class="va-panel__body">
                    <table-shimmer :titleHeaders="['#', 'Student', 'Opportunity', 'Hours', 'Status', 'Submitted', 'Actions']" :rows="6" />
                </div>
                <div v-else-if="hourSubmissions.length === 0" class="va-empty">
                    <font-awesome-icon :icon="['fas', 'hourglass-half']" class="va-empty__icon" />
                    <p class="va-empty__title">No hour submissions found</p>
                    <p class="va-empty__text">Submissions appear here after students complete volunteering.</p>
                </div>
                <div v-else>
                    <div class="overflow-x-auto">
                        <table class="min-w-full text-left text-sm">
                            <thead>
                                <tr class="va-thead-row">
                                    <th class="va-th va-th-num">#</th>
                                    <th class="va-th">Student</th>
                                    <th class="va-th">Opportunity</th>
                                    <th class="va-th">Hours</th>
                                    <th class="va-th">Status</th>
                                    <th class="va-th">Submitted</th>
                                    <th class="va-th">Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                <tr v-for="(sub, index) in hourSubmissions" :key="sub.id" class="va-row">
                                    <td class="va-td va-td-num">{{ paginatedRowNumber(hoursPaginator, index) }}</td>
                                    <td class="va-td font-medium text-gray-900">{{ sub.user && sub.user.name }}</td>
                                    <td class="va-td text-gray-700">{{ sub.opportunity && sub.opportunity.title }}</td>
                                    <td class="va-td">
                                        <span class="va-hours-pill">{{ formatHours(sub.actual_hours) }}</span>
                                    </td>
                                    <td class="va-td">
                                        <span class="va-badge" :class="'va-badge--' + sub.status">{{ formatStatus(sub.status) }}</span>
                                    </td>
                                    <td class="va-td text-gray-500 whitespace-nowrap">{{ formatDateTime(sub.submitted_at) }}</td>
                                    <td class="va-td">
                                        <router-link
                                            v-if="sub.opportunity"
                                            :to="'/volunteering-and-rewards-hub/' + sub.opportunity.token + '?hours=' + sub.id"
                                            class="va-action-btn"
                                            title="Review"
                                        >
                                            <font-awesome-icon :icon="['fas', 'external-link-alt']" />
                                        </router-link>
                                    </td>
                                </tr>
                            </tbody>
                        </table>
                    </div>
                    <div class="va-panel__footer">
                        <pagination v-if="hoursPaginator" :paginator-info="hoursPaginator" @page-changed="onHoursPageChange" />
                    </div>
                </div>
            </section>
        </div>

        <!-- Create/Edit Modal -->
        <volunteering-styled-modal
            :visible="showFormModal"
            :title="editingId ? 'Edit Opportunity' : 'Create Opportunity'"
            :subtitle="editingId ? 'Update opportunity details, schedules, and attachments.' : 'Set up a new volunteering opportunity for students.'"
            :icon="editingId ? ['fas', 'pen'] : ['fas', 'plus']"
            width="800px"
            height="85vh"
            @close="closeFormModal"
        >
            <form class="space-y-6" @submit.prevent="saveOpportunity">
                    <div class="va-form-section">
                        <h3 class="va-form-section__title">Basic Information</h3>
                        <div class="space-y-4">
                            <div>
                                <label class="va-label">Title *</label>
                                <input v-model="form.title" class="va-input w-full" required />
                            </div>
                            <div>
                                <label class="va-label">Description *</label>
                                <textarea v-model="form.description" class="va-input w-full" rows="4" required></textarea>
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="va-label">Application Deadline *</label>
                                    <input v-model="form.application_deadline" type="datetime-local" class="va-input w-full" required />
                                </div>
                                <div>
                                    <label class="va-label">Max Volunteers</label>
                                    <input v-model="form.max_volunteers" type="number" min="1" class="va-input w-full" placeholder="Unlimited if empty" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="va-form-section">
                        <h3 class="va-form-section__title">Location</h3>
                        <div class="space-y-4">
                            <div>
                                <label class="va-label">Location Name *</label>
                                <input v-model="form.location_name" class="va-input w-full" required />
                            </div>
                            <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label class="va-label">Address</label>
                                    <input v-model="form.address" class="va-input w-full" />
                                </div>
                                <div>
                                    <label class="va-label">Google Maps URL</label>
                                    <input v-model="form.google_maps_location" class="va-input w-full" />
                                </div>
                            </div>
                        </div>
                    </div>

                    <div class="va-form-section">
                        <h3 class="va-form-section__title">Schedules *</h3>
                        <div class="space-y-3">
                            <div v-for="(slot, idx) in form.schedules" :key="idx" class="va-schedule-row">
                                <label class="va-schedule-field">
                                    <span>Date</span>
                                    <input v-model="slot.date" type="date" class="va-input" :min="scheduleMinDate" required />
                                </label>
                                <label class="va-schedule-field">
                                    <span>From</span>
                                    <input v-model="slot.start_time" type="time" class="va-input" required />
                                </label>
                                <label class="va-schedule-field">
                                    <span>To</span>
                                    <input v-model="slot.end_time" type="time" class="va-input" required />
                                </label>
                                <button
                                    v-if="form.schedules.length > 1"
                                    type="button"
                                    class="va-action-btn va-action-btn--danger va-schedule-remove"
                                    title="Remove schedule"
                                    @click="removeSchedule(idx)"
                                >
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </button>
                            </div>
                            <div class="flex flex-wrap items-center gap-3">
                                <button type="button" class="va-btn-ghost" @click="addSchedule">
                                    <font-awesome-icon :icon="['fas', 'plus']" />
                                    <span>Add Schedule</span>
                                </button>
                                <span class="va-hours-pill">Expected: {{ calculatedExpectedHours }}</span>
                            </div>
                        </div>
                    </div>

                    <div class="va-form-section">
                        <h3 class="va-form-section__title">Options &amp; Attachments</h3>
                        <div class="space-y-4">
                            <label class="va-checkbox-row">
                                <input v-model="form.allow_hours_exceed_expected" type="checkbox" />
                                <span>Allow actual hours to exceed expected hours</span>
                            </label>
                            <div v-if="form.existing_attachments && form.existing_attachments.length > 0" class="va-attachment-list">
                                <div v-for="(att, idx) in form.existing_attachments" :key="'existing-' + idx" class="va-attachment-item">
                                    <input :id="'keep-att-' + idx" v-model="att.keep" type="checkbox" />
                                    <label :for="'keep-att-' + idx" class="flex-1">{{ att.name || att.original_filename || 'Attachment' }}</label>
                                    <button
                                        v-if="att.file_path && editingId"
                                        type="button"
                                        class="va-link"
                                        :disabled="previewLoading && previewAttachment && previewAttachment.file_path === att.file_path"
                                        @click="openAttachmentPreview(att)"
                                    >
                                        {{ previewLoading && previewAttachment && previewAttachment.file_path === att.file_path ? 'Loading...' : 'View' }}
                                    </button>
                                </div>
                            </div>
                            <div v-for="(att, idx) in form.new_attachments" :key="'att-' + idx" class="va-attachment-row">
                                <input v-model="att.name" placeholder="Display name" class="va-input flex-1" />
                                <input type="file" accept=".pdf,.jpg,.jpeg,.png" class="va-file-input" @change="onAttachmentFile($event, idx)" />
                                <button type="button" class="va-action-btn va-action-btn--danger" @click="form.new_attachments.splice(idx, 1)">
                                    <font-awesome-icon :icon="['fas', 'times']" />
                                </button>
                            </div>
                            <button type="button" class="va-btn-ghost" @click="addAttachment">
                                <font-awesome-icon :icon="['fas', 'paperclip']" />
                                <span>Add Attachment</span>
                            </button>
                        </div>
                    </div>

            </form>
            <template #footer>
                <div class="va-modal-footer-actions">
                    <button type="button" class="va-btn-outline" @click="closeFormModal">Cancel</button>
                    <button type="button" class="va-btn-primary" :disabled="saving" @click="saveOpportunity">
                        {{ saving ? 'Saving...' : (editingId ? 'Update Opportunity' : 'Create Opportunity') }}
                    </button>
                </div>
            </template>
        </volunteering-styled-modal>

        <!-- Attachment preview (same pattern as Adpoly campus process) -->
        <volunteering-styled-modal
            :visible="!!previewAttachment"
            :title="previewAttachment ? (previewAttachment.name || previewAttachment.original_filename || 'Attachment') : 'Attachment'"
            :subtitle="previewAttachment ? (previewAttachment.original_filename || '') : ''"
            :icon="previewAttachmentIsPdf ? ['fas', 'file-pdf'] : ['fas', 'search-plus']"
            width="960px"
            height="85vh"
            flush-body
            @close="closeAttachmentPreview"
        >
            <div class="va-preview-scroll">
                <div class="va-preview-meta">
                    <span class="va-preview-chip">
                        <font-awesome-icon :icon="previewAttachmentIsPdf ? ['fas', 'file-pdf'] : ['fas', 'image']" />
                        {{ previewAttachmentIsPdf ? 'PDF document' : 'Image' }}
                    </span>
                </div>
                <div
                    class="va-preview-frame"
                    :class="{
                        'va-preview-frame--image': previewAttachmentIsImage,
                        'va-preview-frame--pdf': previewAttachmentIsPdf,
                    }"
                >
                    <div v-if="previewLoading" class="va-preview-state">
                        <font-awesome-icon :icon="['fas', 'spinner']" spin class="va-preview-state__icon" />
                        <span>Loading preview…</span>
                    </div>
                    <img
                        v-else-if="previewAttachmentIsImage && previewDisplayUrl"
                        :src="previewDisplayUrl"
                        :alt="previewAttachment ? previewAttachment.name : 'Attachment'"
                        class="va-preview-img"
                    />
                    <iframe
                        v-else-if="previewAttachmentIsPdf && previewDisplayUrl"
                        :src="previewDisplayUrl"
                        class="va-preview-iframe"
                        title="PDF preview"
                    />
                    <div v-else class="va-preview-state">
                        <font-awesome-icon :icon="['fas', 'exclamation-circle']" class="va-preview-state__icon va-preview-state__icon--warn" />
                        <span>Preview unavailable.</span>
                    </div>
                </div>
            </div>
            <template #footer>
                <div class="va-modal-footer-actions">
                    <button type="button" class="va-btn-outline" @click="closeAttachmentPreview">Close</button>
                    <button
                        type="button"
                        class="va-btn-primary"
                        :disabled="!previewDisplayUrl || previewDownloading"
                        @click="downloadPreviewAttachment"
                    >
                        <font-awesome-icon :icon="['fas', previewDownloading ? 'spinner' : 'download']" :spin="previewDownloading" class="mr-2" />
                        {{ previewDownloading ? 'Downloading…' : 'Download' }}
                    </button>
                </div>
            </template>
        </volunteering-styled-modal>

        <resend-volunteering-email-modal
            v-if="resendEmailModal && resendEmailItem"
            :opportunity-token="resendEmailItem.token"
            :opportunity-title="resendEmailItem.title"
            @close="closeResendEmail"
            @done="onResendEmailDone"
        />
    </div>
</template>

<script>
import gql from 'graphql-tag';
import Pagination from '@/components/shared/pagination.vue';
import TableShimmer from '@/components/shared/TableShimmer.vue';
import VolunteeringStyledModal from '@/components/volunteering/VolunteeringStyledModal.vue';
import ResendVolunteeringEmailModal from './components/ResendVolunteeringEmailModal.vue';

const OPP_QUERY = gql`
  query VolunteeringOpportunities($first: Int, $page: Int, $scope: String, $status: String) {
    volunteeringOpportunities(first: $first, page: $page, scope: $scope, status: $status) {
      error error_code message
      paginatorInfo { total currentPage lastPage perPage }
      data { id token title status application_deadline max_volunteers pending_applications_count approved_applications_count created_by_user { id name } }
    }
  }
`;

const APP_QUERY = gql`
  query VolunteeringApplications($first: Int, $page: Int, $status: String, $studentSearch: String, $opportunityId: ID) {
    volunteeringApplications(
      first: $first
      page: $page
      status: $status
      student_search: $studentSearch
      opportunity_id: $opportunityId
    ) {
      error paginatorInfo { total currentPage lastPage perPage }
      data { id status student_id submitted_at user { name } opportunity { token title } }
    }
  }
`;

const HOURS_QUERY = gql`
  query VolunteeringHourSubmissions($first: Int, $page: Int, $status: String) {
    volunteeringHourSubmissions(first: $first, page: $page, status: $status) {
      error paginatorInfo { total currentPage lastPage perPage }
      data { id status actual_hours submitted_at user { name } opportunity { token title } }
    }
  }
`;

const CREATE_MUTATION = gql`
  mutation CreateVolunteeringOpportunity($input: CreateVolunteeringOpportunityInput!) {
    createVolunteeringOpportunity(input: $input) { error message data { id token } }
  }
`;

const UPDATE_MUTATION = gql`
  mutation UpdateVolunteeringOpportunity($id: ID!, $input: UpdateVolunteeringOpportunityInput!) {
    updateVolunteeringOpportunity(id: $id, input: $input) { error message }
  }
`;

const DELETE_MUTATION = gql`
  mutation DeleteVolunteeringOpportunity($id: ID!) {
    deleteVolunteeringOpportunity(id: $id) { error message }
  }
`;

const STATUS_MUTATION = gql`
  mutation UpdateVolunteeringStatus($id: ID!, $status: String!) {
    updateVolunteeringOpportunityStatus(id: $id, status: $status) { error message }
  }
`;

const OPP_BY_TOKEN_QUERY = gql`
  query VolunteeringOpportunityByToken($token: String!) {
    volunteeringOpportunityByToken(token: $token) {
      error message
      data {
        id token title description location_name address google_maps_location
        application_deadline max_volunteers allow_hours_exceed_expected
        schedules { start_at end_at }
        attachments { name file_path original_filename }
      }
    }
  }
`;

const EXPORT_MUTATION = gql`
  mutation ExportVolunteeringReport($status: String) {
    exportVolunteeringReport(status: $status) {
      error message
      data { download_url file_name row_count }
    }
  }
`;

const ATTACHMENT_CONTENT_MUTATION = gql`
  mutation GetVolunteeringOpportunityAttachmentContent($opportunity_id: ID!, $file_path: String!) {
    getVolunteeringOpportunityAttachmentContent(opportunity_id: $opportunity_id, file_path: $file_path) {
      error message
      data { content file_type filename }
    }
  }
`;

export default {
    name: 'VolunteeringAdminView',
    components: { Pagination, TableShimmer, VolunteeringStyledModal, ResendVolunteeringEmailModal },
    data() {
        return {
            activeSection: 'opportunities',
            sections: [
                { value: 'opportunities', label: 'Opportunities', icon: ['fas', 'bullhorn'], badge: '' },
                { value: 'applications', label: 'Applications', icon: ['fas', 'clipboard-list'], badge: '' },
                { value: 'hours', label: 'Hour Submissions', icon: ['fas', 'clock'], badge: '' },
            ],
            stats: {
                opportunities: 0,
                published: 0,
                pendingApplications: 0,
                pendingHours: 0,
            },
            opportunities: [],
            applications: [],
            hourSubmissions: [],
            oppPaginator: null, appPaginator: null, hoursPaginator: null,
            oppPage: 1, appPage: 1, hoursPage: 1,
            listScope: 'mine', statusFilter: '', appStatusFilter: '', hoursStatusFilter: '',
            studentSearch: '',
            appOpportunityFilter: null,
            appOpportunityFilterTitle: '',
            loading: false, saving: false, exporting: false,
            showFormModal: false, editingId: null,
            form: this.emptyForm(),
            previewAttachment: null,
            previewDisplayUrl: null,
            previewFileType: '',
            previewLoading: false,
            previewDownloading: false,
            resendEmailModal: false,
            resendEmailItem: null,
        };
    },
    computed: {
        canCreate() { return this.hasPermission('volunteering-opportunity-create'); },
        canDelete() { return this.hasPermission('volunteering-opportunity-delete'); },
        canPublish() { return this.hasPermission('volunteering-opportunity-publish'); },
        canViewAll() { return this.hasPermission('view-all-volunteering-opportunities'); },
        isSuperAdmin() { return this.$store.getters.role === 'superadmin'; },
        opportunityTableHeaders() {
            if (this.isSuperAdmin) {
                return ['#', 'Title', 'Created By', 'Status', 'Applications', 'Deadline', 'Actions'];
            }
            return ['#', 'Title', 'Status', 'Applications', 'Deadline', 'Actions'];
        },
        // Direct permission check — does NOT auto-allow superadmin
        canResendVolunteeringEmail() {
            const permissions = this.$store.getters.permissions;
            if (!permissions) {
                return false;
            }
            const permissionsArray = permissions.split(',');
            for (let i = 0; i < permissionsArray.length; i++) {
                if (permissionsArray[i].trim() === 'volunteering-resend-email') {
                    return true;
                }
            }
            return false;
        },
        previewAttachmentIsImage() {
            return this.isImageAttachment(this.previewAttachment, this.previewFileType);
        },
        previewAttachmentIsPdf() {
            return this.isPdfAttachment(this.previewAttachment, this.previewFileType);
        },
        calculatedExpectedHours() {
            var total = 0;
            var self = this;
            this.form.schedules.forEach(function (s) {
                var start = self.scheduleSlotAsDate(s, s.start_time);
                var end = self.scheduleSlotAsDate(s, s.end_time);
                if (start && end && end > start) {
                    total += (end - start) / 3600000;
                }
            });
            return total.toFixed(1) + ' hrs';
        },
        scheduleMinDate() {
            if (!this.form.application_deadline) {
                return '';
            }
            var deadline = new Date(this.form.application_deadline);
            if (isNaN(deadline.getTime())) {
                return '';
            }
            var pad = function (num) {
                return num < 10 ? '0' + num : String(num);
            };
            return deadline.getFullYear() + '-' + pad(deadline.getMonth() + 1) + '-' + pad(deadline.getDate());
        },
    },
    watch: {
        activeSection(val) {
            if (val === 'opportunities') this.fetchOpportunities();
            if (val === 'applications') this.fetchApplications();
            if (val === 'hours') this.fetchHours();
        },
    },
    created() {
        this.applyRouteFilters();
        if (this.activeSection === 'applications') {
            this.fetchApplications();
        } else if (this.activeSection === 'hours') {
            this.fetchHours();
        } else {
            this.fetchOpportunities();
        }
        this.fetchAdminStats();
    },
    methods: {
        hasPermission(p) {
            const perms = (this.$store.getters.permissions || '').split(',');
            return perms.includes(p);
        },
        applyRouteFilters() {
            var query = this.$route && this.$route.query ? this.$route.query : {};
            if (query.section === 'applications' && this.hasPermission('volunteering-application-index')) {
                this.activeSection = 'applications';
            }
            if (query.opportunity_id) {
                this.appOpportunityFilter = String(query.opportunity_id);
                this.appOpportunityFilterTitle = query.opportunity_title
                    ? String(query.opportunity_title)
                    : '';
                this.activeSection = 'applications';
            }
        },
        clearOpportunityApplicationFilter() {
            this.appOpportunityFilter = null;
            this.appOpportunityFilterTitle = '';
            this.appPage = 1;
            this.fetchApplications();
            if (this.$route && this.$route.query && (this.$route.query.opportunity_id || this.$route.query.section)) {
                this.$router.replace({ path: '/volunteering-and-rewards-hub' });
            }
        },
        onAppFiltersChange() {
            this.appPage = 1;
            this.fetchApplications();
        },
        goToSection(section) {
            this.activeSection = section;
            if (section === 'applications') {
                this.appOpportunityFilter = null;
                this.appOpportunityFilterTitle = '';
                this.appStatusFilter = 'pending';
                this.appPage = 1;
                this.fetchApplications();
            }
            if (section === 'hours') {
                this.hoursStatusFilter = 'pending';
                this.fetchHours();
            }
        },
        updateTabBadges() {
            const self = this;
            this.sections.forEach(function (tab) {
                if (tab.value === 'applications') {
                    tab.badge = self.stats.pendingApplications > 0 ? String(self.stats.pendingApplications) : '';
                } else if (tab.value === 'hours') {
                    tab.badge = self.stats.pendingHours > 0 ? String(self.stats.pendingHours) : '';
                } else {
                    tab.badge = '';
                }
            });
        },
        async fetchAdminStats() {
            if (!this.hasPermission('volunteering-opportunity-index')) return;
            try {
                const scope = this.listScope;
                const results = await Promise.all([
                    this.$apollo.query({
                        query: OPP_QUERY,
                        variables: { first: 1, page: 1, scope: scope, status: null },
                        fetchPolicy: 'network-only',
                    }),
                    this.$apollo.query({
                        query: OPP_QUERY,
                        variables: { first: 1, page: 1, scope: scope, status: 'published' },
                        fetchPolicy: 'network-only',
                    }),
                    this.hasPermission('volunteering-application-index')
                        ? this.$apollo.query({
                            query: APP_QUERY,
                            variables: { first: 1, page: 1, status: 'pending', studentSearch: null },
                            fetchPolicy: 'network-only',
                        })
                        : Promise.resolve(null),
                    this.hasPermission('volunteering-hours-index')
                        ? this.$apollo.query({
                            query: HOURS_QUERY,
                            variables: { first: 1, page: 1, status: 'pending' },
                            fetchPolicy: 'network-only',
                        })
                        : Promise.resolve(null),
                ]);

                const oppData = results[0].data && results[0].data.volunteeringOpportunities;
                const publishedData = results[1].data && results[1].data.volunteeringOpportunities;
                const appData = results[2] && results[2].data && results[2].data.volunteeringApplications;
                const hoursData = results[3] && results[3].data && results[3].data.volunteeringHourSubmissions;

                this.stats.opportunities = (oppData && oppData.paginatorInfo && oppData.paginatorInfo.total) || 0;
                this.stats.published = (publishedData && publishedData.paginatorInfo && publishedData.paginatorInfo.total) || 0;
                this.stats.pendingApplications = (appData && appData.paginatorInfo && appData.paginatorInfo.total) || 0;
                this.stats.pendingHours = (hoursData && hoursData.paginatorInfo && hoursData.paginatorInfo.total) || 0;
                this.updateTabBadges();
            } catch (e) {
                console.log(e);
            }
        },
        emptyForm() {
            return {
                title: '', description: '', location_name: '', address: '', google_maps_location: '',
                application_deadline: '', max_volunteers: '', allow_hours_exceed_expected: false,
                schedules: [{ date: '', start_time: '', end_time: '' }], new_attachments: [], existing_attachments: [],
            };
        },
        async fetchOpportunities() {
            if (!this.hasPermission('volunteering-opportunity-index')) return;
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: OPP_QUERY,
                    variables: { first: 10, page: this.oppPage, scope: this.listScope, status: this.statusFilter || null },
                    fetchPolicy: 'network-only',
                });
                this.opportunities = data.volunteeringOpportunities.data || [];
                this.oppPaginator = data.volunteeringOpportunities.paginatorInfo;
            } catch (e) { console.log(e); }
            this.loading = false;
            this.fetchAdminStats();
        },
        async fetchApplications() {
            if (!this.hasPermission('volunteering-application-index')) return;
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: APP_QUERY,
                    variables: {
                        first: 10,
                        page: this.appPage,
                        status: this.appStatusFilter || null,
                        studentSearch: this.studentSearch || null,
                        opportunityId: this.appOpportunityFilter || null,
                    },
                    fetchPolicy: 'network-only',
                });
                this.applications = data.volunteeringApplications.data || [];
                this.appPaginator = data.volunteeringApplications.paginatorInfo;
                if (
                    this.appOpportunityFilter
                    && !this.appOpportunityFilterTitle
                    && this.applications.length
                    && this.applications[0].opportunity
                    && this.applications[0].opportunity.title
                ) {
                    this.appOpportunityFilterTitle = this.applications[0].opportunity.title;
                }
            } catch (e) { console.log(e); }
            this.loading = false;
            this.fetchAdminStats();
        },
        async fetchHours() {
            if (!this.hasPermission('volunteering-hours-index')) return;
            this.loading = true;
            try {
                const { data } = await this.$apollo.query({
                    query: HOURS_QUERY,
                    variables: { first: 10, page: this.hoursPage, status: this.hoursStatusFilter || null },
                    fetchPolicy: 'network-only',
                });
                this.hourSubmissions = data.volunteeringHourSubmissions.data || [];
                this.hoursPaginator = data.volunteeringHourSubmissions.paginatorInfo;
            } catch (e) { console.log(e); }
            this.loading = false;
            this.fetchAdminStats();
        },
        onOppPageChange(p) { this.oppPage = p; this.fetchOpportunities(); },
        onAppPageChange(p) { this.appPage = p; this.fetchApplications(); },
        onHoursPageChange(p) { this.hoursPage = p; this.fetchHours(); },
        openCreateModal() { this.editingId = null; this.form = this.emptyForm(); this.showFormModal = true; },
        closeFormModal() {
            this.closeAttachmentPreview();
            this.showFormModal = false;
        },
        addSchedule() { this.form.schedules.push({ date: '', start_time: '', end_time: '' }); },
        removeSchedule(i) { this.form.schedules.splice(i, 1); },
        normalizeTimeValue(time) {
            if (!time) return '';
            return time.length === 5 ? time + ':00' : time;
        },
        scheduleSlotAsDate(slot, time) {
            if (!slot || !slot.date || !time) return null;
            var normalized = this.normalizeTimeValue(time);
            var date = new Date(slot.date + 'T' + normalized);
            if (isNaN(date.getTime())) return null;
            return date;
        },
        toDateParts(value) {
            if (!value) return { date: '', time: '' };
            var date = new Date(value);
            if (isNaN(date.getTime())) return { date: '', time: '' };
            var pad = function (num) { return String(num).padStart(2, '0'); };
            return {
                date: date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate()),
                time: pad(date.getHours()) + ':' + pad(date.getMinutes()),
            };
        },
        toScheduleFormSlot(slot) {
            var startParts = this.toDateParts(slot && slot.start_at);
            var endParts = this.toDateParts(slot && slot.end_at);
            return {
                date: startParts.date || endParts.date || '',
                start_time: startParts.time || '',
                end_time: endParts.time || '',
            };
        },
        buildScheduleDateTime(date, time) {
            if (!date || !time) return '';
            return date + ' ' + this.normalizeTimeValue(time);
        },
        validateSchedules() {
            if (!this.form.application_deadline) {
                return 'Application deadline is required before setting schedules.';
            }
            var deadline = new Date(this.form.application_deadline);
            if (isNaN(deadline.getTime())) {
                return 'Invalid application deadline.';
            }
            if (!this.form.schedules || this.form.schedules.length === 0) {
                return 'At least one schedule is required.';
            }
            for (var i = 0; i < this.form.schedules.length; i++) {
                var slot = this.form.schedules[i];
                var slotLabel = 'Schedule #' + (i + 1);
                if (!slot.date || !slot.start_time || !slot.end_time) {
                    return slotLabel + ': date, from time, and to time are required.';
                }
                var start = this.scheduleSlotAsDate(slot, slot.start_time);
                var end = this.scheduleSlotAsDate(slot, slot.end_time);
                if (!start || !end) {
                    return slotLabel + ': invalid date or time.';
                }
                if (end.getTime() <= start.getTime()) {
                    return slotLabel + ': to time must be after from time.';
                }
                if (start.getTime() <= deadline.getTime()) {
                    return slotLabel + ': from time must be after the application deadline.';
                }
            }
            return '';
        },
        addAttachment() { this.form.new_attachments.push({ name: '', file: null }); },
        onAttachmentFile(e, idx) { this.form.new_attachments[idx].file = e.target.files[0]; },
        attachmentSourceName(att) {
            if (!att) return '';
            return String(att.original_filename || att.name || att.file_path || '').toLowerCase();
        },
        isImageAttachment(att, mime) {
            if (mime && String(mime).indexOf('image/') === 0) return true;
            return /\.(jpe?g|png)$/i.test(this.attachmentSourceName(att));
        },
        isPdfAttachment(att, mime) {
            if (mime === 'application/pdf') return true;
            return /\.pdf$/i.test(this.attachmentSourceName(att));
        },
        blobFromBase64(payload) {
            if (!payload || !payload.content) {
                throw new Error('File content is empty.');
            }
            var binaryString = atob(payload.content);
            var bytes = new Uint8Array(binaryString.length);
            for (var i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            var mime = payload.file_type || 'application/octet-stream';
            return {
                blob: new Blob([bytes], { type: mime }),
                mime: mime,
                filename: payload.filename || 'download',
            };
        },
        revokePreviewDisplayUrl() {
            if (this.previewDisplayUrl) {
                try {
                    URL.revokeObjectURL(this.previewDisplayUrl);
                } catch (e) { /* ignore */ }
            }
            this.previewDisplayUrl = null;
        },
        closeAttachmentPreview() {
            this.revokePreviewDisplayUrl();
            this.previewAttachment = null;
            this.previewFileType = '';
            this.previewLoading = false;
            this.previewDownloading = false;
        },
        async fetchAttachmentPayload(att) {
            var response = await this.$apollo.mutate({
                mutation: ATTACHMENT_CONTENT_MUTATION,
                variables: {
                    opportunity_id: String(this.editingId),
                    file_path: att.file_path,
                },
            });
            var payload = response.data && response.data.getVolunteeringOpportunityAttachmentContent;
            if (!payload || payload.error || !payload.data) {
                throw new Error((payload && payload.message) || 'Failed to load attachment.');
            }
            return payload.data;
        },
        async openAttachmentPreview(att) {
            if (!att || !att.file_path || !this.editingId) return;
            this.revokePreviewDisplayUrl();
            this.previewAttachment = {
                name: att.name || '',
                file_path: att.file_path,
                original_filename: att.original_filename || '',
            };
            this.previewFileType = '';
            this.previewLoading = true;
            try {
                var data = await this.fetchAttachmentPayload(att);
                var file = this.blobFromBase64(data);
                this.previewFileType = file.mime;
                if (!this.isImageAttachment(att, file.mime) && !this.isPdfAttachment(att, file.mime)) {
                    this.triggerBlobDownload(file.blob, file.filename);
                    this.closeAttachmentPreview();
                    return;
                }
                this.previewDisplayUrl = URL.createObjectURL(file.blob);
            } catch (e) {
                this.closeAttachmentPreview();
                if (this.$swal) {
                    this.$swal.fire({ icon: 'error', title: 'Error', text: (e && e.message) || 'Failed to load attachment.' });
                }
            } finally {
                this.previewLoading = false;
            }
        },
        triggerBlobDownload(blob, filename) {
            var url = URL.createObjectURL(blob);
            var link = document.createElement('a');
            link.href = url;
            link.download = filename || 'download';
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            setTimeout(function () { URL.revokeObjectURL(url); }, 60000);
        },
        async downloadPreviewAttachment() {
            if (!this.previewAttachment || !this.previewAttachment.file_path || !this.editingId) return;
            this.previewDownloading = true;
            try {
                var data = await this.fetchAttachmentPayload(this.previewAttachment);
                var file = this.blobFromBase64(data);
                this.triggerBlobDownload(file.blob, file.filename);
            } catch (e) {
                if (this.$swal) {
                    this.$swal.fire({ icon: 'error', title: 'Error', text: (e && e.message) || 'Failed to download attachment.' });
                }
            } finally {
                this.previewDownloading = false;
            }
        },
        editOpportunity(item) {
            const self = this;
            this.editingId = item.id;
            this.form = this.emptyForm();
            this.showFormModal = true;
            this.saving = true;
            this.$apollo.query({
                query: OPP_BY_TOKEN_QUERY,
                variables: { token: item.token },
                fetchPolicy: 'network-only',
            }).then(function (result) {
                const payload = result.data && result.data.volunteeringOpportunityByToken;
                if (!payload || payload.error || !payload.data) {
                    if (self.$swal) self.$swal.fire({ icon: 'error', title: 'Error', text: (payload && payload.message) || 'Failed to load opportunity.' });
                    return;
                }
                const opp = payload.data;
                self.form.title = opp.title || '';
                self.form.description = opp.description || '';
                self.form.location_name = opp.location_name || '';
                self.form.address = opp.address || '';
                self.form.google_maps_location = opp.google_maps_location || '';
                self.form.application_deadline = self.toDateTimeLocal(opp.application_deadline);
                self.form.max_volunteers = opp.max_volunteers ? String(opp.max_volunteers) : '';
                self.form.allow_hours_exceed_expected = !!opp.allow_hours_exceed_expected;
                self.form.schedules = (opp.schedules && opp.schedules.length > 0)
                    ? opp.schedules.map(function (s) {
                        return self.toScheduleFormSlot(s);
                    })
                    : [{ date: '', start_time: '', end_time: '' }];
                self.form.existing_attachments = (opp.attachments || []).map(function (a) {
                    return {
                        name: a.name || a.original_filename || '',
                        file_path: a.file_path || '',
                        original_filename: a.original_filename || '',
                        keep: true,
                    };
                });
            }).catch(function (e) {
                if (self.$swal) self.$swal.fire({ icon: 'error', title: 'Error', text: e.message || 'Failed to load opportunity.' });
            }).finally(function () {
                self.saving = false;
            });
        },
        toDateTimeLocal(value) {
            if (!value) return '';
            const date = new Date(value);
            if (isNaN(date.getTime())) return '';
            const pad = function (num) { return String(num).padStart(2, '0'); };
            return date.getFullYear() + '-' + pad(date.getMonth() + 1) + '-' + pad(date.getDate())
                + 'T' + pad(date.getHours()) + ':' + pad(date.getMinutes());
        },
        buildInput() {
            const self = this;
            const schedules = this.form.schedules.map(function (s) {
                return {
                    start_at: self.buildScheduleDateTime(s.date, s.start_time),
                    end_at: self.buildScheduleDateTime(s.date, s.end_time),
                };
            });
            const attachments = this.form.new_attachments.filter(function (a) { return a.name && a.file; });
            const input = {
                title: this.form.title,
                description: this.form.description,
                location_name: this.form.location_name,
                address: this.form.address || null,
                google_maps_location: this.form.google_maps_location || null,
                application_deadline: this.formatDateTimeForGraphQL(this.form.application_deadline),
                max_volunteers: this.form.max_volunteers ? parseInt(this.form.max_volunteers, 10) : null,
                allow_hours_exceed_expected: this.form.allow_hours_exceed_expected,
                schedules: schedules,
                new_attachments: attachments,
            };
            if (this.editingId && this.form.existing_attachments) {
                input.keep_attachment_paths = this.form.existing_attachments
                    .filter(function (a) { return a.keep && a.file_path; })
                    .map(function (a) { return a.file_path; });
            }
            return input;
        },
        formatDateTimeForGraphQL(dateTimeLocal) {
            if (!dateTimeLocal) return '';
            const date = new Date(dateTimeLocal);
            const pad = function (num) {
                return String(num).padStart(2, '0');
            };
            const y = date.getFullYear();
            const m = pad(date.getMonth() + 1);
            const d = pad(date.getDate());
            const h = pad(date.getHours());
            const i = pad(date.getMinutes());
            const s = pad(date.getSeconds());
            return y + '-' + m + '-' + d + ' ' + h + ':' + i + ':' + s;
        },
        async saveOpportunity() {
            const scheduleError = this.validateSchedules();
            if (scheduleError) {
                if (this.$swal) this.$swal.fire({ icon: 'error', title: 'Invalid schedule', text: scheduleError });
                return;
            }
            this.saving = true;
            try {
                const input = this.buildInput();
                if (this.editingId) {
                    await this.$apollo.mutate({ mutation: UPDATE_MUTATION, variables: { id: this.editingId, input: input } });
                } else {
                    await this.$apollo.mutate({ mutation: CREATE_MUTATION, variables: { input: input } });
                }
                this.showSuccessToast(this.editingId ? 'Opportunity updated successfully.' : 'Opportunity created successfully.');
                this.closeFormModal();
                this.fetchOpportunities();
            } catch (e) {
                if (this.$swal) this.$swal.fire({ icon: 'error', title: 'Error', text: e.message || 'Failed to save.' });
            }
            this.saving = false;
        },
        showSuccessToast(message) {
            if (!this.$swal) return;
            const Toast = this.$swal.mixin({
                toast: true,
                position: 'top-end',
                showConfirmButton: false,
                timer: 2500,
                timerProgressBar: true,
            });
            Toast.fire({
                icon: 'success',
                title: message || 'Success',
            });
        },
        async updateStatus(item, status) {
            if (status === 'published' && this.$swal) {
                const result = await this.$swal.fire({
                    title: 'Publish opportunity?',
                    text: 'This will notify eligible students and open the opportunity for applications.',
                    icon: 'question',
                    showCancelButton: true,
                    confirmButtonText: 'Publish',
                    cancelButtonText: 'Cancel',
                });
                if (!result.value) return;
            }
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: STATUS_MUTATION,
                    variables: { id: item.id, status: status },
                });
                const payload = data && data.updateVolunteeringOpportunityStatus;
                if (payload && payload.error) {
                    throw new Error(payload.message || 'Failed to update status.');
                }
                if (status === 'published') {
                    this.showSuccessToast('Opportunity published successfully.');
                }
                this.fetchOpportunities();
            } catch (e) {
                if (this.$swal) this.$swal.fire({ icon: 'error', title: 'Error', text: e.message || 'Failed to update status.' });
            }
        },
        openResendEmail(item) {
            this.resendEmailItem = item;
            this.resendEmailModal = true;
        },
        closeResendEmail() {
            this.resendEmailModal = false;
            this.resendEmailItem = null;
        },
        onResendEmailDone(message) {
            this.closeResendEmail();
            this.showSuccessToast(message || 'Volunteering emails queued successfully.');
        },
        async exportReport() {
            this.exporting = true;
            try {
                const { data } = await this.$apollo.mutate({
                    mutation: EXPORT_MUTATION,
                    variables: { status: this.appStatusFilter || null },
                });
                const payload = data && data.exportVolunteeringReport;
                if (!payload || payload.error || !payload.data || !payload.data.download_url) {
                    throw new Error((payload && payload.message) || 'Export failed.');
                }
                window.open(payload.data.download_url, '_blank');
                if (this.$swal) {
                    this.$swal.fire({
                        icon: 'success',
                        title: 'Export Ready',
                        text: 'Exported ' + (payload.data.row_count || 0) + ' rows.',
                    });
                }
            } catch (e) {
                if (this.$swal) this.$swal.fire({ icon: 'error', title: 'Error', text: e.message || 'Failed to export.' });
            }
            this.exporting = false;
        },
        async deleteOpportunity(item) {
            if (this.$swal) {
                const result = await this.$swal.fire({ title: 'Delete?', text: 'This cannot be undone.', icon: 'warning', showCancelButton: true });
                if (!result.value) return;
            }
            try {
                await this.$apollo.mutate({ mutation: DELETE_MUTATION, variables: { id: item.id } });
                this.fetchOpportunities();
            } catch (e) {
                if (this.$swal) this.$swal.fire({ icon: 'error', title: 'Error', text: e.message });
            }
        },
        formatStatus(s) { return s ? s.charAt(0).toUpperCase() + s.slice(1) : ''; },
        formatDateTime(d) { return d ? new Date(d).toLocaleString() : ''; },
        formatHours(h) { return parseFloat(h || 0).toFixed(1) + ' hrs'; },
        paginatedRowNumber(paginator, index) {
            const start = paginator && paginator.firstItem ? paginator.firstItem : 1;
            return start + index;
        },
    },
};
</script>

<style scoped lang="scss">
.va-page {
    background: linear-gradient(135deg, #f3f4f6 0%, #eff6ff 100%);
}

/* Buttons */
.va-btn-primary {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #fff;
    background: linear-gradient(180deg, #2563eb 0%, #1d4ed8 100%);
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: box-shadow 0.2s;
    &:hover { box-shadow: 0 4px 12px -2px rgba(37, 99, 235, 0.45); }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.va-btn-ghost {
    display: inline-flex;
    align-items: center;
    gap: 0.375rem;
    padding: 0.5rem 0.875rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #374151;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.2s;
    &:hover { background: #f9fafb; border-color: #d1d5db; }
    &:disabled { opacity: 0.6; cursor: not-allowed; }
}
.va-btn-outline {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    padding: 0.5rem 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #6b7280;
    background: transparent;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    cursor: pointer;
    &:hover { background: #f3f4f6; }
}

/* Stat cards */
.va-stat-card {
    display: flex;
    align-items: center;
    gap: 0.875rem;
    padding: 1.25rem 1rem;
    border-radius: 1rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    transition: box-shadow 0.2s, transform 0.2s;
}
.va-stat-card--pending,
.va-stat-card--hours {
    cursor: pointer;
    &:hover {
        transform: translateY(-2px);
        box-shadow: 0 6px 20px -6px rgba(0, 0, 0, 0.1);
    }
}
.va-stat-card__icon {
    flex-shrink: 0;
    width: 2.75rem;
    height: 2.75rem;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.75rem;
    font-size: 1.125rem;
}
.va-stat-card__value {
    font-size: 1.5rem;
    font-weight: 800;
    line-height: 1.2;
    margin: 0;
    color: #1e293b;
}
.va-stat-card__label {
    font-size: 0.8125rem;
    font-weight: 600;
    margin: 0.125rem 0 0;
    color: #475569;
}
.va-stat-card--total .va-stat-card__icon { background: #dbeafe; color: #2563eb; }
.va-stat-card--published { border-left: 3px solid #059669; }
.va-stat-card--published .va-stat-card__icon { background: #d1fae5; color: #059669; }
.va-stat-card--pending { border-left: 3px solid #d97706; }
.va-stat-card--pending .va-stat-card__icon { background: #fef3c7; color: #d97706; }
.va-stat-card--hours { border-left: 3px solid #7c3aed; }
.va-stat-card--hours .va-stat-card__icon { background: #ede9fe; color: #7c3aed; }

/* Tabs */
.va-tabs-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    padding: 0.375rem;
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 0.75rem;
}
.va-tab {
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.625rem 1rem;
    font-size: 0.8125rem;
    font-weight: 600;
    color: #64748b;
    background: transparent;
    border: none;
    border-radius: 0.5rem;
    cursor: pointer;
    transition: all 0.15s;
    &:hover { background: #f1f5f9; color: #334155; }
}
.va-tab--active {
    background: #2563eb;
    color: #fff;
    &:hover { background: #1d4ed8; color: #fff; }
}
.va-tab__icon { font-size: 0.875rem; }
.va-tab__badge {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 1.25rem;
    height: 1.25rem;
    padding: 0 0.35rem;
    font-size: 0.6875rem;
    font-weight: 700;
    border-radius: 9999px;
    background: rgba(255, 255, 255, 0.25);
    color: inherit;
}
.va-tab:not(.va-tab--active) .va-tab__badge {
    background: #fee2e2;
    color: #dc2626;
}

/* Panel */
.va-panel {
    background: #fff;
    border: 1px solid #e5e7eb;
    border-radius: 1rem;
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
}
.va-panel__header {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
    padding: 1.5rem 1.5rem 1rem;
    border-bottom: 1px solid #f1f5f9;
}
.va-panel__title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #111827;
    margin: 0;
}
.va-panel__subtitle {
    font-size: 0.8125rem;
    color: #6b7280;
    margin: 0.25rem 0 0;
}
.va-panel__body { padding: 1rem 1.5rem 1.5rem; }
.va-panel__footer {
    padding: 1rem 1.5rem;
    border-top: 1px solid #f1f5f9;
}

/* Filters */
.va-filters {
    display: flex;
    flex-wrap: wrap;
    align-items: center;
    gap: 0.5rem;
}
.va-select,
.va-input {
    padding: 0.5rem 0.75rem;
    font-size: 0.8125rem;
    color: #374151;
    background: #fff;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
    &:focus {
        outline: none;
        border-color: #2563eb;
        box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.15);
    }
}
.va-search-wrap {
    position: relative;
    display: flex;
    align-items: center;
}
.va-search-icon {
    position: absolute;
    left: 0.75rem;
    color: #9ca3af;
    font-size: 0.75rem;
    pointer-events: none;
}
.va-search-input {
    padding-left: 2rem;
    min-width: 12rem;
}

/* Table */
.va-thead-row {
    background: #f8fafc;
    border-bottom: 2px solid #e2e8f0;
}
.va-th-num,
.va-td-num {
    width: 3rem;
    text-align: center;
    color: #64748b;
    font-weight: 600;
}
.va-th {
    padding: 0.75rem 1.25rem;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    color: #64748b;
}
.va-row {
    border-bottom: 1px solid #f1f5f9;
    transition: background 0.15s;
    &:hover { background: #f8fafc; }
}
.va-td {
    padding: 0.75rem 1.25rem;
    font-size: 0.8125rem;
    color: #374151;
    vertical-align: middle;
}
.va-name-link {
    font-weight: 600;
    color: #1e3a8a;
    text-decoration: none;
    &:hover { color: #2563eb; text-decoration: underline; }
}

.va-apps-link {
    color: inherit;
    text-decoration: none;
    &:hover {
        .font-semibold { color: #2563eb; }
        .va-inline-pill { background: #dbeafe; color: #1d4ed8; }
    }
}

/* Action buttons */
.va-action-btn {
    width: 2rem;
    height: 2rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    border-radius: 0.5rem;
    border: none;
    background: #f1f5f9;
    color: #475569;
    cursor: pointer;
    font-size: 0.8125rem;
    text-decoration: none;
    transition: all 0.15s;
    &:hover { background: #e0e7ff; color: #4338ca; }
}
.va-action-btn--danger:hover { background: #fee2e2; color: #dc2626; }
.va-action-btn--success:hover { background: #d1fae5; color: #059669; }

.va-text-link {
    display: inline-flex;
    align-items: center;
    padding: 0.35rem 0.65rem;
    border-radius: 0.5rem;
    border: 1px solid #bfdbfe;
    background: #eff6ff;
    color: #1d4ed8;
    font-size: 0.75rem;
    font-weight: 650;
    text-decoration: none;
    cursor: pointer;
    white-space: nowrap;
    transition: background 0.15s ease, border-color 0.15s ease;
    &:hover {
        background: #dbeafe;
        border-color: #93c5fd;
        color: #1e40af;
        text-decoration: none;
    }
}

.va-filter-chip-bar {
    padding: 0 1.25rem 0.85rem;
}
.va-filter-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.4rem 0.65rem;
    border-radius: 9999px;
    background: #eff6ff;
    border: 1px solid #bfdbfe;
    color: #1e40af;
    font-size: 0.75rem;
    font-weight: 600;
}
.va-filter-chip strong {
    font-weight: 700;
    max-width: 18rem;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
}
.va-filter-chip__clear {
    width: 1.25rem;
    height: 1.25rem;
    border: none;
    border-radius: 9999px;
    background: #dbeafe;
    color: #1d4ed8;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-size: 0.65rem;
    padding: 0;
    &:hover { background: #bfdbfe; }
}

/* Badges */
.va-badge {
    display: inline-flex;
    align-items: center;
    padding: 0.2rem 0.5rem;
    border-radius: 9999px;
    font-size: 0.6875rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: 0.02em;
}
.va-badge--draft { background: #f3f4f6; color: #374151; }
.va-badge--published { background: #d1fae5; color: #065f46; }
.va-badge--closed { background: #fef3c7; color: #92400e; }
.va-badge--completed { background: #dbeafe; color: #1e40af; }
.va-badge--archived { background: #f1f5f9; color: #64748b; }
.va-badge--pending { background: #fef3c7; color: #92400e; }
.va-badge--approved { background: #d1fae5; color: #065f46; }
.va-badge--rejected { background: #fee2e2; color: #991b1b; }
.va-badge--returned { background: #ffedd5; color: #9a3412; }
.va-badge--withdrawn { background: #f1f5f9; color: #64748b; }

.va-inline-pill {
    display: inline-block;
    margin-left: 0.5rem;
    padding: 0.1rem 0.4rem;
    font-size: 0.6875rem;
    font-weight: 600;
    border-radius: 9999px;
    background: #fef3c7;
    color: #92400e;
}
.va-hours-pill {
    display: inline-flex;
    padding: 0.2rem 0.5rem;
    font-size: 0.75rem;
    font-weight: 700;
    border-radius: 0.375rem;
    background: #dbeafe;
    color: #1e40af;
}

/* Empty state */
.va-empty {
    padding: 4rem 1.5rem;
    text-align: center;
}
.va-empty__icon {
    font-size: 2.5rem;
    color: #cbd5e1;
    margin-bottom: 1rem;
}
.va-empty__title {
    font-size: 1.125rem;
    font-weight: 700;
    color: #1f2937;
    margin: 0 0 0.25rem;
}
.va-empty__text {
    font-size: 0.875rem;
    color: #6b7280;
    margin: 0;
}

/* Form */
.va-form-section {
    padding-bottom: 1rem;
    border-bottom: 1px solid #f1f5f9;
    &:last-of-type { border-bottom: none; }
}
.va-form-section__title {
    font-size: 0.875rem;
    font-weight: 700;
    color: #1e293b;
    margin: 0 0 1rem;
    text-transform: uppercase;
    letter-spacing: 0.04em;
}
.va-label {
    display: block;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.04em;
    margin-bottom: 0.375rem;
}
.va-schedule-row {
    display: flex;
    flex-wrap: wrap;
    align-items: flex-end;
    gap: 0.5rem;
}
.va-schedule-field {
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
    flex: 1 1 7.5rem;
    min-width: 7rem;
    font-size: 0.7rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
    letter-spacing: 0.03em;
}
.va-schedule-remove {
    margin-bottom: 0.15rem;
}
.va-checkbox-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.875rem;
    color: #374151;
    cursor: pointer;
}
.va-attachment-list {
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
}
.va-attachment-item,
.va-attachment-row {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    padding: 0.5rem 0.75rem;
    background: #f8fafc;
    border: 1px solid #e2e8f0;
    border-radius: 0.5rem;
    font-size: 0.8125rem;
}
.va-link {
    font-size: 0.8125rem;
    font-weight: 600;
    color: #2563eb;
    text-decoration: none;
    background: none;
    border: none;
    padding: 0;
    cursor: pointer;
    &:hover { text-decoration: underline; }
    &:disabled { opacity: 0.6; cursor: wait; }
}
.va-file-input {
    font-size: 0.75rem;
    color: #6b7280;
}
.va-modal-footer-actions {
    display: flex;
    justify-content: flex-end;
    gap: 0.75rem;
}

/* Attachment preview modal */
.va-preview-scroll {
    display: flex;
    flex-direction: column;
    min-height: 60vh;
    height: 100%;
    padding: 1.25rem 1.5rem;
    background: linear-gradient(180deg, #f8fafc 0%, #eff6ff 100%);
}
.va-preview-meta {
    display: flex;
    flex-wrap: wrap;
    gap: 0.5rem;
    margin-bottom: 1rem;
    flex-shrink: 0;
}
.va-preview-chip {
    display: inline-flex;
    align-items: center;
    gap: 0.4rem;
    padding: 0.35rem 0.75rem;
    border-radius: 9999px;
    background: #dbeafe;
    color: #1d4ed8;
    font-size: 0.75rem;
    font-weight: 600;
}
.va-preview-frame {
    flex: 1 1 auto;
    min-height: 24rem;
    border-radius: 1rem;
    border: 1px solid #e2e8f0;
    background: #f8fafc;
    overflow: hidden;
    display: flex;
    align-items: center;
    justify-content: center;
}
.va-preview-frame--image {
    padding: 1rem;
    background-color: #f1f5f9;
}
.va-preview-frame--pdf {
    background: #fff;
    padding: 0;
    align-items: stretch;
}
.va-preview-img {
    max-width: 100%;
    max-height: 100%;
    width: auto;
    height: auto;
    object-fit: contain;
}
.va-preview-iframe {
    width: 100%;
    height: 100%;
    min-height: 24rem;
    border: 0;
}
.va-preview-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 0.75rem;
    padding: 2rem;
    color: #64748b;
    font-size: 0.875rem;
    font-weight: 600;
}
.va-preview-state__icon {
    font-size: 1.5rem;
    color: #2563eb;
}
.va-preview-state__icon--warn {
    color: #d97706;
}
</style>
